class PromoGiftManager {
  constructor(config, collectionCache) {
    this.config = config.promoCampaign || null;
    this.collectionCache = collectionCache;
    this.enableGiftCampaign = this.config?.enabled === true;
    this.promoGiftsSubscriptionOnly = this.config?.subscriptionOnly === true;
    this.eligibleCollectionHandle = this.config?.eligibleCollectionHandle || null;
    this.eligibleProductIds = null;
    this.promoGiftRules = GiftCart.parsePromoGiftRules(this.config?.rules || []);
    this.promoGiftVariantIds = this.#buildVariantIds();
    this.allowExtraVariantIds = new Set(
      (this.config?.gifts || [])
        .filter((gift) => gift.allowExtra)
        .map((gift) => parseInt(gift.variantId, 10))
        .filter((id) => !isNaN(id))
    );
    this.isValidatingCart = false;
    this.cachedCart = null;
    this.previousCartItemCount = 0;
  }

  #buildVariantIds() {
    const ids = new Set();
    for (const gift of this.config?.gifts || []) {
      const vid = parseInt(gift.variantId, 10);
      if (!isNaN(vid)) ids.add(vid);
    }
    for (const rule of this.promoGiftRules) {
      if (!isNaN(rule.variantId)) ids.add(rule.variantId);
    }
    return [...ids];
  }

  get enabled() {
    return this.promoGiftVariantIds.length > 0;
  }

  async resolveEligibility() {
    let ids = null;
    if (this.eligibleCollectionHandle) {
      ids = await this.collectionCache.getProductIds(this.eligibleCollectionHandle);
    }
    this.eligibleProductIds = this.collectionCache.mergeExtraIds(ids, this.config?.eligibleProductIds);
  }

  updateCachedCart(cartData) {
    if (cartData?.items) {
      this.previousCartItemCount = this.cachedCart ? this.cachedCart.items.length : 0;
      this.cachedCart = cartData;
    }
  }

  isPromoGiftVariant(variantId) {
    return this.promoGiftVariantIds.some((id) => parseInt(id, 10) === parseInt(variantId, 10));
  }

  isAllowExtraPromoGiftVariant(variantId) {
    return this.allowExtraVariantIds.has(parseInt(variantId, 10));
  }

  isStrictPromoGiftVariant(variantId) {
    return this.isPromoGiftVariant(variantId) && !this.isAllowExtraPromoGiftVariant(variantId);
  }

  getStrictPromoGiftVariantIds() {
    return this.promoGiftVariantIds.filter((id) => !this.allowExtraVariantIds.has(parseInt(id, 10)));
  }

  getPromoGiftItems(cart) {
    if (!cart?.items) return [];
    return cart.items.filter((item) => this.isPromoGiftVariant(item.variant_id));
  }

  getStrictPromoGiftItems(cart) {
    if (!cart?.items) return [];
    return cart.items.filter((item) => this.isStrictPromoGiftVariant(item.variant_id));
  }

  hasOtherProducts(cart) {
    if (!cart?.items) return false;
    return cart.items.some((item) => !this.isStrictPromoGiftVariant(item.variant_id));
  }

  #getEligibleQuantity(cart) {
    return GiftCart.countEligibleQuantityInCart(
      cart,
      this.promoGiftVariantIds,
      this.eligibleProductIds,
      this.promoGiftsSubscriptionOnly
    );
  }

  #getTargetQuantities(cart) {
    return GiftCart.resolvePromoGiftTargetQuantities(
      this.#getEligibleQuantity(cart),
      this.promoGiftRules,
      this.promoGiftVariantIds
    );
  }

  #shouldRemoveStrictPromoGifts(cart) {
    const strictIds = this.getStrictPromoGiftVariantIds();
    if (strictIds.length === 0) return false;

    const hasStrictInCart = cart.items.some((item) =>
      strictIds.some((id) => parseInt(id, 10) === parseInt(item.variant_id, 10))
    );
    if (!hasStrictInCart) return false;
    if (!this.hasOtherProducts(cart)) return true;
    return this.#getEligibleQuantity(cart) === 0;
  }

  getSyncUpdates(cart) {
    if (!this.enabled || !cart?.items) return null;

    if (!this.enableGiftCampaign) {
      if (this.getStrictPromoGiftItems(cart).length === 0) return null;
      const updates = {};
      for (const vid of this.getStrictPromoGiftVariantIds()) {
        updates[vid] = 0;
      }
      return { updates };
    }

    if (this.#shouldRemoveStrictPromoGifts(cart)) {
      const updates = {};
      for (const vid of this.getStrictPromoGiftVariantIds()) {
        updates[vid] = 0;
      }
      return { updates };
    }

    const targetQuantities = this.#getTargetQuantities(cart);
    const updates = GiftCart.getPromoGiftQuantityUpdates(
      cart,
      this.promoGiftVariantIds,
      targetQuantities,
      [...this.allowExtraVariantIds]
    );

    if (Object.keys(updates).length === 0) return null;
    return { updates };
  }

  async enforceCart(cart) {
    if (!this.enabled || !cart) return { cart, changed: false };

    const syncResult = this.getSyncUpdates(cart);
    if (!syncResult?.updates) return { cart, changed: false };

    const updatedCart = await GiftCart.applyCartUpdates(syncResult.updates);
    if (!updatedCart) return { cart, changed: false };

    this.updateCachedCart(updatedCart);
    return { cart: updatedCart, changed: true };
  }

  async enforcePromoGiftRules(cart) {
    const result = await this.enforceCart(cart);
    return result.cart;
  }
}

window.PromoGiftManager = PromoGiftManager;
